import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

/**
 * Portal button variants map 1:1 onto Bootstrap / Metronic `btn-*` classes.
 * Templates should prefer this wrapper so pages stop hard-coding Metronic class names.
 */
export type PortalButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'dark'
  | 'light'
  | 'light-primary'
  | 'light-success'
  | 'light-info'
  | 'light-warning'
  | 'light-danger'
  | 'light-dark'
  | 'active-light'
  | 'active-light-primary'
  | 'outline'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-danger'
  | 'flush'
  | 'link';

export type PortalButtonSize = 'sm' | 'lg' | null;

/**
 * Content layout preference for PortalUI actions.
 *
 * - `icon` — default for chrome / toolbar / row actions (square `btn-icon`, requires `ariaLabel`)
 * - `icon-label` — exception when the action needs a visible text label next to the icon
 * - `label` — text-only (form submits, dialog confirms, empty-state CTAs)
 * - `pencil-edit-slim` — glyph-only pencil, no glass tile (inline rename next to a title)
 */
export type PortalButtonLayout = 'icon' | 'icon-label' | 'label' | 'pencil-edit-slim';

const PENCIL_EDIT_SLIM_ICON = 'ki-solid ki-pencil';

/** Icon-only light variants that resolve to quiet glass + optional semantic hover. */
const GLASS_ICON_VARIANTS = new Set<PortalButtonVariant>([
  'light',
  'light-primary',
  'light-success',
  'light-info',
  'light-warning',
  'light-danger',
  'light-dark',
  'active-light',
  'active-light-primary',
]);

const GLASS_ICON_HOVER: Partial<Record<PortalButtonVariant, string>> = {
  'light-primary': 'portal-glass-icon-action--hover-primary',
  'light-success': 'portal-glass-icon-action--hover-success',
  'light-danger': 'portal-glass-icon-action--hover-danger',
  'light-warning': 'portal-glass-icon-action--hover-warning',
  'light-info': 'portal-glass-icon-action--hover-primary',
  'active-light-primary': 'portal-glass-icon-action--hover-primary',
};

const GLASS_ICON_OPT_OUT = new Set([
  'portal-glass-icon-action--no-auto',
  'portal-dark-chrome__glass-btn',
  'portal-notifications__glass-btn',
]);

/**
 * Thin PortalUI wrapper around Bootstrap `btn` classes.
 * No visual restyle — ownership layer for Phase 2 Metronic-exit.
 *
 * Prefer icon-only where possible; use `icon-label` / `label` only when text is required.
 *
 * @example
 * ```html
 * <!-- Preferred: icon-only -->
 * <app-portal-button
 *   layout="icon"
 *   variant="light"
 *   size="sm"
 *   iconClass="ki-solid ki-arrows-circle"
 *   ariaLabel="Vernieuwen"
 *   (pressed)="refresh()">
 * </app-portal-button>
 *
 * <!-- Exception: icon + text -->
 * <app-portal-button
 *   layout="icon-label"
 *   variant="primary"
 *   size="sm"
 *   iconClass="ki-solid ki-plus"
 *   (pressed)="add()">
 *   Klant toevoegen
 * </app-portal-button>
 * ```
 */
@Component({
  selector: 'app-portal-button',
  templateUrl: './portal-button.component.html',
  styleUrls: ['./portal-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalButtonComponent implements OnChanges {
  @Input() variant: PortalButtonVariant = 'primary';
  @Input() size: PortalButtonSize = null;

  /**
   * Preferred content layout. Defaults to `label` when unset so existing text buttons keep working.
   * New chrome/actions should set `layout="icon"`.
   */
  @Input() layout: PortalButtonLayout | null = null;

  /**
   * @deprecated Prefer `layout="icon"`. Kept for existing call sites (e.g. list-panel).
   * When `true` and `layout` is unset, resolves to icon-only.
   */
  @Input() icon = false;

  /**
   * Optional Keenicon classes (`ki-solid ki-plus`). Renders before projected content.
   * Prefer this for icon-only / icon-label so templates stay concise.
   */
  @Input() iconClass: string | null = null;

  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input({ transform: booleanAttribute }) disabled = false;
  /** Shows a compact spinner; keeps the control disabled while true. */
  @Input() loading = false;
  /** Required for `layout="icon"` (and deprecated `[icon]="true"`). */
  @Input() ariaLabel: string | null = null;
  @Input() ariaExpanded: boolean | null = null;
  @Input() title: string | null = null;
  /** Extra utility classes (e.g. `w-100`, `me-2`) — do not pass `btn-*` here. */
  @Input() extraClass: string | string[] | null = null;

  @Output() pressed = new EventEmitter<MouseEvent>();

  @HostBinding('class.portal-button-host')
  readonly hostClass = true;

  @HostBinding('class.portal-button-host--icon')
  get hostIcon(): boolean {
    return this.resolvedLayout === 'icon';
  }

  @HostBinding('class.portal-button-host--icon-label')
  get hostIconLabel(): boolean {
    return this.resolvedLayout === 'icon-label';
  }

  @HostBinding('class.portal-button-host--pencil-edit-slim')
  get hostPencilEditSlim(): boolean {
    return this.resolvedLayout === 'pencil-edit-slim';
  }

  get resolvedLayout(): PortalButtonLayout {
    if (this.layout) {
      return this.layout;
    }
    return this.icon ? 'icon' : 'label';
  }

  get isIconOnly(): boolean {
    return this.resolvedLayout === 'icon' || this.resolvedLayout === 'pencil-edit-slim';
  }

  get resolvedIconClass(): string | null {
    if (this.iconClass) {
      return this.iconClass;
    }
    return this.resolvedLayout === 'pencil-edit-slim' ? PENCIL_EDIT_SLIM_ICON : null;
  }

  get isDisabled(): boolean {
    return this.disabled || this.loading;
  }

  get resolvedTitle(): string | null {
    return this.title ?? (this.isIconOnly ? this.ariaLabel : null);
  }

  get buttonClasses(): string[] {
    const extraTokens = this.normalizeExtraClassTokens();
    const applyGlassIcon = this.shouldApplyGlassIconAction(extraTokens);
    const slimEdit = this.resolvedLayout === 'pencil-edit-slim';
    const resolvedVariant = slimEdit ? 'flush' : applyGlassIcon ? 'light' : this.variant;

    const classes = ['btn', `btn-${resolvedVariant}`];
    if (this.size && !slimEdit) {
      classes.push(`btn-${this.size}`);
    }
    if (this.isIconOnly) {
      classes.push('btn-icon');
    }
    if (slimEdit) {
      classes.push('portal-button--pencil-edit-slim');
    }
    if (this.resolvedLayout === 'icon-label') {
      classes.push('portal-button--icon-label');
    }
    if (this.loading) {
      classes.push('portal-button--loading');
    }
    if (applyGlassIcon) {
      classes.push('portal-glass-icon-action');
      const hoverClass = GLASS_ICON_HOVER[this.variant];
      if (hoverClass) {
        classes.push(hoverClass);
      }
    }
    classes.push(...extraTokens);
    return [...new Set(classes)];
  }

  private normalizeExtraClassTokens(): string[] {
    if (typeof this.extraClass === 'string' && this.extraClass.trim()) {
      return this.extraClass.trim().split(/\s+/);
    }
    if (Array.isArray(this.extraClass)) {
      return this.extraClass.filter(Boolean);
    }
    return [];
  }

  private shouldApplyGlassIconAction(extraTokens: string[]): boolean {
    if (!this.isIconOnly || this.resolvedLayout === 'pencil-edit-slim') {
      return false;
    }
    if (!GLASS_ICON_VARIANTS.has(this.variant)) {
      return false;
    }
    if (extraTokens.some((token) => GLASS_ICON_OPT_OUT.has(token))) {
      return false;
    }
    return true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['layout'] || changes['icon'] || changes['ariaLabel']) &&
      this.isIconOnly &&
      !this.ariaLabel
    ) {
      console.warn(
        '[app-portal-button] layout="icon" requires ariaLabel for accessibility.'
      );
    }
  }

  onClick(event: MouseEvent): void {
    if (this.isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.pressed.emit(event);
  }
}
